import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../Components/Common/HelpArticleLink";
import TeamPermissionMatrixView from "../../Components/Studio/TeamPermissions/TeamPermissionMatrixView";
import { HELP_ARTICLE_TEAM_PERMISSIONS } from "../../helpers/helpCenter";
import {
  getTeamPermissions,
  TeamPermissionMatrix,
} from "../../services/permissionService";

const TeamPermissionsPage = () => {
  const { t } = useTranslation();

  const [matrix, setMatrix] = useState<TeamPermissionMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    document.title = t("team_permissions.pageTitle");
  }, [t]);

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    setLoadError(false);

    try {
      const response = await getTeamPermissions();
      setMatrix(response.data);
    } catch (error: any) {
      setLoadError(true);
      setMatrix(null);
      toast.error(error?.data || t("team_permissions.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("team_permissions.breadcrumb.title")}
            pageTitle={t("team_permissions.breadcrumb.pageTitle")}
          />
          <div className="d-flex justify-content-end mb-3">
            <HelpArticleLink articlePath={HELP_ARTICLE_TEAM_PERMISSIONS} />
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <div className="flex-grow-1">
                    <h4 className="card-title mb-1">{t("team_permissions.card.title")}</h4>
                    <p className="text-muted mb-0 small">{t("team_permissions.card.subtitle")}</p>
                  </div>
                  <Button color="soft-primary" tag={Link} to="/users">
                    <i className="ri-user-add-line align-bottom me-1" />
                    {t("team_permissions.actions.manageUsers")}
                  </Button>
                </CardHeader>

                <CardBody>
                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary">{t("team_permissions.loading")}</Spinner>
                    </div>
                  ) : loadError ? (
                    <Alert color="danger" className="mb-0">
                      <p className="mb-2">{t("team_permissions.error.description")}</p>
                      <Button color="primary" size="sm" onClick={loadMatrix}>
                        {t("team_permissions.error.retry")}
                      </Button>
                    </Alert>
                  ) : matrix ? (
                    <TeamPermissionMatrixView matrix={matrix} />
                  ) : null}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TeamPermissionsPage;
